import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence, useAnimationControls, useInView, useReducedMotion } from 'framer-motion';
import { Gift, Target, X, Pointer } from 'lucide-react';
import { haptic } from '../../utils/haptics';

/**
 * Tipp-Anleitung des Skill-Auswählers als kleine Vorführung (Okt 2026).
 *
 * Vorher stand dort ein Satz ("Tippe einmal für ..."), der trotz Schimmer-Effekt
 * überlesen wurde. Jetzt tippt ein Finger eine echte Pille an, und die Pille
 * durchläuft dieselben drei Zustände wie im Katalog: grün mit Geschenk (kann
 * helfen), lila mit Zielscheibe (möchte lernen), grau (abgewählt). Darunter
 * eine Legende mit Tipp-Punkten (•, ••, •••), die im Takt mitleuchtet.
 *
 * Bewegung nur, solange sie hilft: läuft erst, wenn die Karte im Bild ist, hört
 * nach drei Runden auf und sofort, sobald jemand einen echten Skill antippt
 * (`active`). Bei "Bewegung reduzieren" gibt es nur die ruhige Legende. Die
 * Demo-Pille lässt sich selbst antippen, ohne die Auswahl zu verändern.
 */

// Reihenfolge wie handleSkillStateChange: neutral -> offering -> seeking -> neutral
const STEPS = [
  { key: 'neutral', taps: 0 },
  { key: 'offering', taps: 1, label: 'Kann helfen', Icon: Gift },
  { key: 'seeking', taps: 2, label: 'Möchte lernen', Icon: Target },
  { key: 'cleared', taps: 3, label: 'Abwählen', Icon: X },
];

const STEP_MS = 1700;
const MAX_ROUNDS = 3;

const PILL_STYLES = {
  offering: 'bg-offer-soft text-foreground glass-hairline-emerald',
  seeking: 'bg-primary-soft text-foreground glass-hairline-violet',
  neutral: 'bg-surface text-muted-foreground glass-hairline',
};

const pillStyle = (key) => PILL_STYLES[key] || PILL_STYLES.neutral;

const TapDots = ({ count, lit, tone }) => (
  <span className="flex items-center gap-0.5" aria-hidden="true">
    {Array.from({ length: count }, (_, i) => (
      <span
        key={i}
        className={`h-1.5 w-1.5 rounded-full transition-colors duration-300 ${lit ? tone : 'bg-muted-foreground/40'}`}
      />
    ))}
  </span>
);

TapDots.propTypes = {
  count: PropTypes.number.isRequired,
  lit: PropTypes.bool,
  tone: PropTypes.string,
};

const LEGEND_TONE = {
  offering: { dot: 'bg-offer', icon: 'text-offer', ring: 'glass-hairline-emerald bg-offer-soft' },
  seeking: { dot: 'bg-primary', icon: 'text-primary', ring: 'glass-hairline-violet bg-primary-soft' },
  cleared: { dot: 'bg-foreground', icon: 'text-foreground', ring: 'glass-hairline bg-surface-raised' },
};

const SkillTapGuide = ({ sampleSkill, active = true }) => {
  const reduceMotion = useReducedMotion();
  const cardRef = useRef(null);
  const inView = useInView(cardRef, { amount: 0.6 });
  const [step, setStep] = useState(0);
  const [tapId, setTapId] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const ticks = useRef(0);
  const press = useAnimationControls();

  const playing = autoplay && active && inView && !reduceMotion;

  useEffect(() => {
    if (!playing) return undefined;
    const timer = setInterval(() => {
      ticks.current += 1;
      if (ticks.current > MAX_ROUNDS * STEPS.length) {
        setAutoplay(false);
        setStep(0);
        return;
      }
      setStep((s) => (s + 1) % STEPS.length);
      setTapId((t) => t + 1);
    }, STEP_MS);
    return () => clearInterval(timer);
  }, [playing]);

  // Ein echter Tipp im Katalog beendet die Vorführung, die Legende bleibt stehen
  useEffect(() => {
    if (!active) {
      setAutoplay(false);
      setStep(0);
    }
  }, [active]);

  // Jeder Tipp drückt die Pille kurz ein (ohne sie neu zu mounten, Fokus bleibt)
  useEffect(() => {
    if (tapId === 0 || reduceMotion) return undefined;
    press.start({
      scale: [1, 0.9, 1.06, 1],
      transition: { duration: 0.45, times: [0, 0.25, 0.6, 1], ease: 'easeOut' },
    });
    return undefined;
  }, [tapId, reduceMotion, press]);

  // Von Hand: nach "Abwählen" geht es wieder mit "Kann helfen" los
  const tryIt = () => {
    setAutoplay(false);
    setStep((s) => (s === STEPS.length - 1 ? 1 : s + 1));
    setTapId((t) => t + 1);
    haptic.selection();
  };

  const current = STEPS[step];
  // "cleared" sieht aus wie neutral, nur die Legende unterscheidet die Schritte
  const pillKey = current.key === 'cleared' ? 'neutral' : current.key;
  const PillIcon = current.key === 'offering' ? Gift : current.key === 'seeking' ? Target : null;
  const demoName = sampleSkill || 'Mathematik';

  return (
    <div ref={cardRef} className="rounded-xl bg-surface p-3 glass-hairline" data-testid="skill-tap-guide">
      <p className="sr-only">
        Tippe einen Skill einmal für „kann helfen“, zweimal für „möchte lernen“, dreimal zum Abwählen.
      </p>

      <p className="text-center text-[11px] font-medium uppercase tracking-wide text-muted-foreground" aria-hidden="true">
        {autoplay && active && !reduceMotion ? 'So geht’s' : 'Probier’s an der Pille aus'}
      </p>

      <div className="mt-2 flex items-center justify-center gap-3">
        <div className="w-10" aria-hidden="true" />
        <div className="relative">
          <motion.button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); tryIt(); }}
            aria-label={`Beispiel-Pille „${demoName}“ zum Ausprobieren, ändert deine Auswahl nicht`}
            className={`relative inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors duration-300 ${pillStyle(pillKey)}`}
            animate={press}
          >
            <span className="relative flex h-3.5 w-3.5 shrink-0 items-center justify-center" aria-hidden="true">
              <AnimatePresence mode="popLayout" initial={false}>
                {PillIcon && (
                  <motion.span
                    key={current.key}
                    initial={reduceMotion ? false : { scale: 0, rotate: -90, opacity: 0 }}
                    animate={{ scale: 1, rotate: 0, opacity: 1 }}
                    exit={reduceMotion ? undefined : { scale: 0, rotate: 90, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 520, damping: 22 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <PillIcon className={`h-3.5 w-3.5 ${current.key === 'offering' ? 'text-offer' : 'text-primary'}`} />
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
            <span className="max-w-[9rem] truncate">{demoName}</span>
            <span
              className={`h-2 w-2 shrink-0 rounded-full transition-colors duration-300 ${
                pillKey === 'offering' ? 'bg-offer' : pillKey === 'seeking' ? 'bg-primary' : 'bg-muted-foreground/40'
              }`}
              aria-hidden="true"
            />
          </motion.button>

          {/* Tipp-Welle und Finger: nur als Bewegung, nicht für Screenreader */}
          {!reduceMotion && tapId > 0 && (
            <motion.span
              key={`ripple-${tapId}`}
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-primary/50"
              initial={{ opacity: 0.9, scale: 1 }}
              animate={{ opacity: 0, scale: 1.35 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          )}
          {playing && (
            <motion.span
              key={`finger-${tapId}`}
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-3 right-2 text-foreground"
              initial={{ y: 6, opacity: 0 }}
              animate={{ y: [6, -2, 2], opacity: [0, 1, 1] }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <Pointer className="h-5 w-5 drop-shadow-sm" strokeWidth={2} />
            </motion.span>
          )}
        </div>

        <div className="w-10 text-left" aria-hidden="true">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={`count-${step}`}
              initial={reduceMotion ? false : { y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={reduceMotion ? undefined : { y: -8, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="block font-display text-lg leading-none text-foreground"
            >
              {current.taps > 0 ? `${current.taps}×` : ''}
            </motion.span>
          </AnimatePresence>
        </div>
      </div>

      <ol className="mt-5 grid grid-cols-3 gap-1.5" aria-hidden="true">
        {STEPS.slice(1).map(({ key, taps, label, Icon }) => {
          const lit = current.key === key;
          const tone = LEGEND_TONE[key];
          return (
            <motion.li
              key={key}
              animate={reduceMotion ? undefined : { scale: lit ? 1.04 : 1, opacity: lit || step === 0 ? 1 : 0.55 }}
              transition={{ type: 'spring', stiffness: 400, damping: 26 }}
              className={`flex flex-col items-center gap-1 rounded-lg px-1 py-2 transition-colors duration-300 ${
                lit ? tone.ring : 'bg-transparent'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <TapDots count={taps} lit={lit || step === 0} tone={tone.dot} />
                <Icon className={`h-3.5 w-3.5 shrink-0 ${tone.icon}`} />
              </span>
              <span className="whitespace-nowrap text-xs font-medium text-foreground">{label}</span>
            </motion.li>
          );
        })}
      </ol>
    </div>
  );
};

SkillTapGuide.propTypes = {
  sampleSkill: PropTypes.string,
  active: PropTypes.bool,
};

export default SkillTapGuide;
